import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  IconButton, Skeleton, Alert, Tooltip, Select, MenuItem,
  FormControl, InputLabel,
} from '@mui/material';
import {
  SearchOutlined, AssignmentOutlined, RefreshOutlined,
  CheckCircleOutlined, CancelOutlined, HourglassEmptyOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';

const PRIMARY = '#1075BA';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('gv_admin_token')}` } });

const STATUS_META = {
  pending:   { color: '#e07b00', label: 'Pending',   icon: <HourglassEmptyOutlined sx={{ fontSize: 15 }} /> },
  approved:  { color: '#00a882', label: 'Approved',  icon: <CheckCircleOutlined    sx={{ fontSize: 15 }} /> },
  rejected:  { color: '#d32f2f', label: 'Rejected',  icon: <CancelOutlined         sx={{ fontSize: 15 }} /> },
  cancelled: { color: '#888',    label: 'Cancelled', icon: <CancelOutlined         sx={{ fontSize: 15 }} /> },
};

export default function SubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [forms, setForms]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [formFilter, setFormFilter]   = useState('');
  const [statusFilter, setStatus]     = useState('');
  const [error, setError]             = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [subRes, formRes] = await Promise.all([
        axios.get(`${API_BASE}/submissions`, authHeader()),
        axios.get(`${API_BASE}/forms`, authHeader()),
      ]);
      setSubmissions(subRes.data.data || []);
      setForms(formRes.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = submissions.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !search || s.submitterEmail?.toLowerCase().includes(q) || s.submitterName?.toLowerCase().includes(q);
    const matchForm   = !formFilter || s.form === formFilter || s.form?._id === formFilter;
    const matchStatus = !statusFilter || s.status === statusFilter;
    return matchSearch && matchForm && matchStatus;
  });

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '1.55rem', fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>Submissions</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {loading ? 'Loading…' : `${submissions.length} submission${submissions.length !== 1 ? 's' : ''} total`}
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ flex: '1 1 220px', maxWidth: 300 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Form</InputLabel>
          <Select value={formFilter} label="Form" onChange={e => setFormFilter(e.target.value)}>
            <MenuItem value="">All forms</MenuItem>
            {forms.map(f => <MenuItem key={f._id} value={f._id}>{f.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={load} size="small" sx={{ border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 2, width: 36, height: 36 }}>
            <RefreshOutlined sx={{ fontSize: 18, color: PRIMARY }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Card sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 2.5 }}>
            {[...Array(5)].map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <Box sx={{ flex: 1 }}><Skeleton width="40%" /><Skeleton width="28%" height={14} /></Box>
                <Skeleton width={80} /><Skeleton width={70} height={22} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 7 }}>
            <AssignmentOutlined sx={{ fontSize: 44, color: alpha(PRIMARY, 0.22), mb: 1.5 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {search || formFilter || statusFilter ? 'No submissions match your filters.' : 'No submissions yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Submitter</TableCell>
                  <TableCell>Form</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(sub => {
                  const s = STATUS_META[sub.status] || STATUS_META.pending;
                  return (
                    <TableRow key={sub._id} hover sx={{ '&:hover': { background: alpha(PRIMARY, 0.025) } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{sub.submitterName || '—'}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub.submitterEmail || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {typeof sub.form === 'object' ? sub.form?.name : (forms.find(f => f._id === sub.form)?.name || '—')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={React.cloneElement(s.icon, { style: { color: s.color } })}
                          label={s.label}
                          sx={{ background: alpha(s.color, 0.09), color: s.color, border: `1px solid ${alpha(s.color, 0.22)}`, fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                          {sub.referenceNumber || sub._id?.slice(-8).toUpperCase()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}
